import React from 'react';
import { createPortal } from 'react-dom';

// Extracted from LessonWorkspace.js (Phase 3 of the platform-health plan:
// split the file mechanically, without changing behavior). Shown when the
// teacher/student selects a word from the lesson text and adds it to the
// dictionary — the AI-generated candidate meanings/variants to pick from.
export default function DictVariantsModal({
  show,
  pendingWord,
  variants,
  currentEntries,
  selectedIndices,
  isVariantAlreadyAdded,
  onToggleVariant,
  onClose,
  onSelectSelected,
  onSelectAll,
}) {
  if (!show || variants.length === 0) return null;

  return createPortal(
    <div
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 2147483647,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'white', borderRadius: 12,
          boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
          maxWidth: 700, width: '90%', maxHeight: '80vh',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          position: 'relative', zIndex: 2147483647,
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 20px 16px', borderBottom: '1px solid #e0e0e0' }}>
          <h2 style={{ margin: 0, fontSize: 20, color: '#333' }}>Select variants for "{pendingWord}"</h2>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: 28, color: '#666', cursor: 'pointer', lineHeight: 1, padding: 0, width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 4 }}
          >×</button>
        </div>
        {/* Variants list */}
        <div style={{ padding: 20, overflowY: 'auto', flex: 1 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
            {variants.map((variant, index) => {
              const alreadyAdded = isVariantAlreadyAdded(variant, currentEntries, pendingWord);
              const checked = selectedIndices.has(index);
              return (
                <div
                  key={index}
                  onClick={() => !alreadyAdded && onToggleVariant(index)}
                  style={{
                    display: 'flex', alignItems: 'flex-start', gap: 12,
                    border: `2px solid ${alreadyAdded ? '#e2e8f0' : checked ? '#22c55e' : '#e0e0e0'}`,
                    borderRadius: 8, padding: 15,
                    cursor: alreadyAdded ? 'default' : 'pointer',
                    background: alreadyAdded ? '#f8fafc' : checked ? '#f0fdf4' : 'white',
                    opacity: alreadyAdded ? 0.65 : 1,
                    transition: 'all 0.15s',
                  }}
                >
                  <div style={{ marginTop: 2, flexShrink: 0 }}>
                    {alreadyAdded ? (
                      <span style={{ fontSize: 16, color: '#22c55e' }}>✓</span>
                    ) : (
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => onToggleVariant(index)}
                        onClick={e => e.stopPropagation()}
                        style={{ width: 16, height: 16, accentColor: '#22c55e', cursor: 'pointer' }}
                      />
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
                      {variant.type && variant.type !== 'regular' && (
                        <span style={{ display: 'inline-block', padding: '3px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: variant.type === 'phrasal verb' ? '#fff3e0' : '#f3e5f5', color: variant.type === 'phrasal verb' ? '#e65100' : '#7b1fa2' }}>
                          {variant.type === 'phrasal verb' ? 'Phrasal verb' : 'Idiom'}
                        </span>
                      )}
                      <span style={{ display: 'inline-block', padding: '3px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: '#e3f2fd', color: '#1976d2' }}>
                        {variant.partOfSpeech || 'N/A'}
                      </span>
                      <span style={{ display: 'inline-block', padding: '3px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: '#e8f5e9', color: '#2e7d32' }}>
                        {variant.level || 'N/A'}
                      </span>
                      {alreadyAdded && (
                        <span style={{ marginLeft: 'auto', fontSize: 11, color: '#22c55e', fontWeight: 600, background: '#f0fdf4', borderRadius: 4, padding: '1px 6px', border: '1px solid #bbf7d0' }}>
                          Already added
                        </span>
                      )}
                    </div>
                    <div style={{ marginTop: 6, fontSize: 14, lineHeight: 1.5 }}>
                      <strong style={{ color: '#333', marginRight: 4 }}>Translation:</strong>{variant.translation || 'N/A'}
                    </div>
                    <div style={{ marginTop: 4, fontSize: 14, lineHeight: 1.5 }}>
                      <strong style={{ color: '#333', marginRight: 4 }}>Example:</strong>{variant.example || 'N/A'}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {/* Action buttons */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, paddingTop: 12, borderTop: '1px solid #e0e0e0' }}>
            <button
              disabled={selectedIndices.size === 0}
              onClick={onSelectSelected}
              style={{
                padding: '10px 20px', fontSize: 15, fontWeight: 600, color: '#fff', border: 'none', borderRadius: 8, cursor: selectedIndices.size > 0 ? 'pointer' : 'not-allowed',
                background: selectedIndices.size > 0 ? '#22c55e' : '#d1d5db',
                opacity: selectedIndices.size > 0 ? 1 : 0.6,
              }}
            >
              Add selected{selectedIndices.size > 0 ? ` (${selectedIndices.size})` : ''}
            </button>
            <button
              onClick={onSelectAll}
              style={{ padding: '10px 20px', fontSize: 15, fontWeight: 600, color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', background: '#2196F3' }}
            >
              Add all
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
